"use client";
import { usePrivy } from "@privy-io/react-auth";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { env } from "@/utils/envs";
import QueryKey from "@/utils/queryKeys";

// --- Types ---

export type ShinkaiIdentityResponse = {
	selectedShinkaiIdentity?: string | null;
	rewards?: boolean;
	isAdmin?: boolean;
};

export type UpdateIdentityPayload = {
	selectedShinkaiIdentity?: string;
	rewards?: boolean;
};

// --- Query Keys ---

export const shinkaiIdentityKeys = {
	all: ["shinkai-identity"] as const,
	detail: (authenticated: boolean) =>
		[...shinkaiIdentityKeys.all, authenticated] as const,
};

// --- API Functions ---

export const fetchShinkaiIdentity = async (
	getAccessToken: () => Promise<string | null>,
): Promise<ShinkaiIdentityResponse> => {
	const token = await getAccessToken();
	if (!token) {
		throw new Error("No access token");
	}

	const response = await fetch(
		`${env.VITE_SHINKAI_WEB_API_URL}/api/shinkai-identity`,
		{
			headers: {
				Authorization: `Bearer ${token}`,
			},
		},
	);

	if (!response.ok) {
		throw new Error("Failed to fetch shinkai identity");
	}

	return (await response.json()) as ShinkaiIdentityResponse;
};

export const updateShinkaiIdentity = async (
	getAccessToken: () => Promise<string | null>,
	payload: UpdateIdentityPayload,
): Promise<ShinkaiIdentityResponse> => {
	const token = await getAccessToken();
	if (!token) {
		throw new Error("No access token");
	}

	const response = await fetch(
		`${env.VITE_SHINKAI_WEB_API_URL}/api/shinkai-identity`,
		{
			method: "POST",
			headers: {
				Authorization: `Bearer ${token}`,
				"Content-Type": "application/json",
			},
			body: JSON.stringify(payload),
		},
	);

	if (!response.ok) {
		const error = await response.json();
		throw new Error(error.error || "Failed to update identity");
	}

	return response.json() as Promise<ShinkaiIdentityResponse>;
};

// --- Query Hooks ---

/**
 * Hook to fetch the user's Shinkai identity data
 */
export const useShinkaiIdentityQuery = () => {
	const { authenticated, ready, getAccessToken } = usePrivy();

	return useQuery<ShinkaiIdentityResponse>({
		queryKey: shinkaiIdentityKeys.detail(authenticated),
		queryFn: async () => {
			if (!authenticated) {
				return { selectedShinkaiIdentity: null, rewards: false };
			}
			return fetchShinkaiIdentity(getAccessToken);
		},
		enabled: ready && authenticated,
		staleTime: 60000, // Cache for 1 minute
	});
};

// --- Mutation Hooks ---

/**
 * Hook to update the selected Shinkai identity
 */
export const useUpdateSelectedIdentity = () => {
	const { authenticated, getAccessToken } = usePrivy();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (identity: string) => {
			if (!authenticated) {
				throw new Error("Not authenticated");
			}
			return updateShinkaiIdentity(getAccessToken, {
				selectedShinkaiIdentity: identity,
			});
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: shinkaiIdentityKeys.all });
		},
	});
};

/**
 * Hook to update the rewards opt-in status
 */
export const useUpdateRewardsOptIn = () => {
	const { authenticated, getAccessToken } = usePrivy();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (rewards: boolean) => {
			if (!authenticated) {
				throw new Error("Not authenticated");
			}
			return updateShinkaiIdentity(getAccessToken, { rewards });
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: shinkaiIdentityKeys.all });
			// Refetch quest progress since the server gates it on rewards opt-in
			queryClient.invalidateQueries({ queryKey: [QueryKey.MY_QUESTS] });
		},
	});
};
